import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiV1 } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { PasswordInput } from '@/components/PasswordInput';
import { Loader2, KeyRound } from 'lucide-react';
import { HireIqLogo } from '@/components/HireIqLogo';
import {
  PASSWORD_POLICY_HINT,
  TurnstileWidget,
  passwordMeetsPolicy,
} from '@/components/TurnstileWidget';

const INVALID_LINK_MESSAGE = 'Invalid or expired password link';

/**
 * Consume invite or forgot token and set a new password (local auth).
 */
export default function SetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get('token') || '', [params]);
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [linkState, setLinkState] = useState(token ? 'pending' : 'invalid');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiV1.get('/auth/config');
        if (!cancelled) setCfg(r.data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setLinkState('invalid');
      setErr(INVALID_LINK_MESSAGE);
      return undefined;
    }
    setLinkState('pending');
    setErr(null);
    (async () => {
      try {
        const r = await apiV1.post('/auth/password/set-status', { token });
        if (cancelled) return;
        if (r.data?.valid) {
          setLinkState('valid');
          setErr(null);
        } else {
          setLinkState('invalid');
          setErr(r.data?.message || INVALID_LINK_MESSAGE);
        }
      } catch (e) {
        if (cancelled) return;
        setLinkState('invalid');
        const detail = e?.response?.data?.detail;
        setErr(typeof detail === 'string' ? detail : INVALID_LINK_MESSAGE);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const onCaptcha = useCallback((t) => setCaptchaToken(t), []);

  const submit = async (ev) => {
    ev.preventDefault();
    if (!token) {
      setErr(INVALID_LINK_MESSAGE);
      return;
    }
    if (!passwordMeetsPolicy(password)) {
      setErr(PASSWORD_POLICY_HINT);
      return;
    }
    if (password !== confirm) {
      setErr('Password confirmation does not match.');
      return;
    }
    if (cfg?.captcha_site_key && !captchaToken) {
      setErr('Please complete the captcha.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await apiV1.post('/auth/password/set', {
        token,
        password,
        captcha_token: captchaToken || undefined,
      });
      nav('/login?passwordSet=1', { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const showForm = linkState === 'valid';

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12">
      <div className="w-full max-w-md rounded-2xl border bg-card p-8 shadow-sm" data-testid="set-password-card">
        <HireIqLogo size="xl" className="mx-auto max-w-[200px] !object-center" />
        {showForm ? (
          <>
            <p className="mt-4 text-lg font-semibold tracking-tight">Set your password</p>
            <p className="text-xs text-muted-foreground">{PASSWORD_POLICY_HINT}</p>
            <form onSubmit={submit} className="mt-6 space-y-3" data-testid="set-password-form">
              <div>
                <label className="text-xs font-medium text-muted-foreground" htmlFor="set-password">New password</label>
                <PasswordInput
                  id="set-password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="set-password-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground" htmlFor="set-password-confirm">Confirm password</label>
                <PasswordInput
                  id="set-password-confirm"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  matchAgainst={password}
                  required
                  data-testid="set-password-confirm-input"
                />
              </div>
              {cfg?.captcha_site_key ? (
                <TurnstileWidget siteKey={cfg.captcha_site_key} onToken={onCaptcha} />
              ) : null}
              <Button type="submit" disabled={busy} className="w-full gap-2" size="lg" data-testid="set-password-submit">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                {busy ? 'Saving…' : 'Save password'}
              </Button>
            </form>
          </>
        ) : linkState === 'pending' ? (
          <p className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground" data-testid="set-password-checking">
            <Loader2 className="h-4 w-4 animate-spin" />
            Checking link…
          </p>
        ) : null}

        {err && linkState !== 'pending' && (
          <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="set-password-error">
            {typeof err === 'string' ? err : JSON.stringify(err)}
          </div>
        )}

        <p className="mt-6 text-center text-xs text-muted-foreground">
          <Link to="/login" className="text-primary underline-offset-2 hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
